import React, { useState, useRef } from 'react'
import { MapPinIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { ShoppingCartIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { normalizeSocialLinks } from '../../../utils/social'
import { buildCloudinaryUrl, buildCloudinarySrcSet } from '../../../utils/cloudinaryImage'
import useCardPWA from '../../../pwa/useCardPWA'
import PWAInstallButton from '../../../components/pwa/PWAInstallButton'

function CatalogTemplate({ vCard, template }) {
  const theme = vCard.theme || {
    primaryColor: '#ec4899',
    accentColor: '#6b7280',
    font: 'Inter'
  }

  const socialLinks = normalizeSocialLinks(vCard.social || {})
  const brandColors = {
    whatsapp: '#25D366',
    facebook: '#1877F2',
    linkedin: '#0A66C2',
    instagram: '#E4405F',
    tiktok: '#000000',
  }
  
  // Manejar products como array u objeto con orden
  const getProductsArray = () => {
    if (!vCard.products) return []
    let arr = []
    if (Array.isArray(vCard.products)) {
      arr = vCard.products
    } else if (typeof vCard.products === 'object') {
      arr = Object.entries(vCard.products).map(([id, product]) => ({ id, ...product }))
    }
    return arr.slice().sort((a, b) => {
      const ao = typeof a.order === 'number' ? a.order : 999999
      const bo = typeof b.order === 'number' ? b.order : 999999
      if (ao !== bo) return ao - bo
      return String(a.name || '').localeCompare(String(b.name || ''))
    })
  }
  
  const products = getProductsArray()
  useCardPWA(vCard)
  const [cartItems, setCartItems] = useState([])
  const [showCart, setShowCart] = useState(false)
  const [showProductDetail, setShowProductDetail] = useState(false)
  const [detailProduct, setDetailProduct] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 })
  const containerRef = useRef(null)
  const imgRef = useRef(null)
  const [baseScale, setBaseScale] = useState(1)

  const getYouTubeEmbedUrl = () => {
    const raw = vCard.videoUrl || ''
    if (!raw || typeof raw !== 'string') return ''
    const url = raw.trim()
    if (url.includes('youtube.com/embed/')) return url
    const youtuShort = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/)
    if (youtuShort && youtuShort[1]) return `https://www.youtube.com/embed/${youtuShort[1]}`
    const watch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/)
    if (watch && watch[1]) return `https://www.youtube.com/embed/${watch[1]}`
    return ''
  }

  const buildWhatsAppUrlForProduct = (product) => {
    const message = `Hola, me gustaría preguntar sobre el producto: ${product.name}${product.desc ? ` - ${product.desc}` : ''}`
    const base = socialLinks.whatsapp || (vCard.phone ? `https://wa.me/${String(vCard.phone).replace(/\D/g, '')}` : null)
    if (!base) return null
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}text=${encodeURIComponent(message)}`
  }

  const addToCart = (product) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id)
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }
      return [...prev, { ...product, quantity: 1 }]
    })
  }

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId))
  }

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const numericPrice = typeof item.price === 'number'
        ? item.price
        : (!isNaN(parseFloat(item.price)) ? parseFloat(item.price) : 0)
      return total + (numericPrice * item.quantity)
    }, 0)
  }

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0)
  }

  const preloadedRef = useRef(new Map())
  const prefetchImage = (url) => {
    if (!url || preloadedRef.current.get(url)) return
    const hi = buildCloudinaryUrl(url, { w: 1200 })
    const img = new Image()
    img.src = hi
    preloadedRef.current.set(url, true)
  }
  const openDetail = (product) => {
    setDetailProduct(product)
    setShowProductDetail(true)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }
  const closeDetail = () => {
    setShowProductDetail(false)
    setDetailProduct(null)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }

  const clampZoom = (z) => Math.min(4, Math.max(1, z))
  const handleWheel = (e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.2 : 0.2
    setZoom(z => clampZoom(z + delta))
  }
  const handleMouseDown = (e) => {
    setDragging(true)
    setLastPos({ x: e.clientX, y: e.clientY })
  }
  const handleMouseMove = (e) => {
    if (!dragging) return
    const dx = e.clientX - lastPos.x
    const dy = e.clientY - lastPos.y
    setLastPos({ x: e.clientX, y: e.clientY })
    setOffset(o => ({ x: o.x + dx, y: o.y + dy }))
  }
  const handleMouseUp = () => setDragging(false)
  const handleMouseLeave = () => setDragging(false)
  const handleDoubleClick = () => {
    setZoom(z => (z >= 3 ? 1 : z + 1))
    if (zoom >= 3) setOffset({ x: 0, y: 0 })
  }
  const getDistance = (touches) => {
    const [a, b] = [touches[0], touches[1]]
    const dx = a.clientX - b.clientX
    const dy = a.clientY - b.clientY
    return Math.sqrt(dx*dx + dy*dy)
  }
  const [pinchStart, setPinchStart] = useState(null)
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setPinchStart(getDistance(e.touches))
    } else if (e.touches.length === 1) {
      setDragging(true)
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStart) {
      const current = getDistance(e.touches)
      const diff = (current - pinchStart) / 200
      setZoom(z => clampZoom(z + diff))
      setPinchStart(current)
    } else if (e.touches.length === 1 && dragging) {
      const t = e.touches[0]
      const dx = t.clientX - lastPos.x
      const dy = t.clientY - lastPos.y
      setLastPos({ x: t.clientX, y: t.clientY })
      setOffset(o => ({ x: o.x + dx, y: o.y + dy }))
    }
  }
  const handleTouchEnd = () => {
    setDragging(false)
    setPinchStart(null)
  }
  const handleImageLoad = () => {
    const c = containerRef.current
    const img = imgRef.current
    if (!c || !img) return
    const cw = c.clientWidth
    const ch = c.clientHeight
    const iw = img.naturalWidth || cw
    const ih = img.naturalHeight || ch
    const cover = Math.max(cw / iw, ch / ih)
    const initial = Math.max(1, cover * 1.1)
    setBaseScale(initial)
    setZoom(initial)
    setOffset({ x: 0, y: 0 })
  }

  return (
    <div 
      className="catalog-template min-h-screen bg-gray-50"
      style={{ fontFamily: theme.font }}
    >
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center relative">
          <div className="flex items-center">
            {(vCard.photoUrl || vCard.logoUrl) && (
              <img src={vCard.photoUrl || vCard.logoUrl} alt={vCard.name} className="w-10 h-10 rounded-full mr-3 object-cover" />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">{vCard.name}</h1>
              <p className="text-sm text-gray-600">{vCard.position}</p>
            </div>
          </div>
          
          
          <button
            onClick={() => setShowCart(!showCart)}
            className="relative p-3 rounded-full hover:bg-gray-100 transition-colors"
            style={{ backgroundColor: theme.primaryColor + '10' }}
          >
            <ShoppingCartIcon className="w-6 h-6" style={{ color: theme.primaryColor }} />
            {getTotalItems() > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center" style={{ backgroundColor: theme.primaryColor }}>
                {getTotalItems()}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="text-white py-12 md:py-16"
        style={{ backgroundImage: `linear-gradient(to right, ${theme.primaryColor || '#111111'}, ${theme.accentColor || theme.primaryColor || '#333333'})` }}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">Catálogo de Productos</h2>
          <p className="text-base md:text-xl text-white/80">Descubre nuestra selección de productos exclusivos</p>
        </div>
      </section>

      {/* Bio (visible en catálogo) */}
      {vCard.bio && (
        <div className="max-w-3xl mx-auto px-4 md:px-6 mt-4 md:mt-8">
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-sm p-4 md:p-6">
            <p className="text-gray-700 text-sm md:text-base text-center leading-relaxed">{vCard.bio}</p>
          </div>
        </div>
      )}

      {/* YouTube Embed (opcional) */}
      {getYouTubeEmbedUrl() && (
        <div className="max-w-4xl mx-auto px-4 md:px-6 mt-6">
          <div className="relative w-full overflow-hidden rounded-2xl shadow-md" style={{ paddingTop: '56.25%' }}>
            <iframe
              src={getYouTubeEmbedUrl()}
              title="YouTube video"
              className="absolute top-0 left-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-center flex-wrap gap-3">
          {socialLinks.whatsapp && (
            <a
              href={socialLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: brandColors.whatsapp, color: '#fff' }}
              aria-label="WhatsApp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
              </svg>
            </a>
          )}
          {socialLinks.facebook && (
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: brandColors.facebook, color: '#fff' }}
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.675 0h-21.35C.6 0 0 .6 0 1.342v21.317C0 23.4.6 24 1.325 24h11.495v-9.294H9.691v-3.622h3.129V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.794.143v3.24l-1.918.001c-1.504 0-1.796.715-1.796 1.764v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.4 24 24 23.4 24 22.659V1.342C24 .6 23.4 0 22.675 0z"/>
              </svg>
            </a>
          )}
          {socialLinks.linkedin && (
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: brandColors.linkedin, color: '#fff' }}
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          )}
          {socialLinks.instagram && (
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundImage: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4, #FEDA75)', color: '#fff' }}
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
              </svg>
            </a>
          )}
          {socialLinks.tiktok && (
            <a
              href={socialLinks.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: brandColors.tiktok, color: '#fff' }}
              aria-label="TikTok"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.004 0h3.043c.214 1.833 1.097 3.221 2.61 4.17 1.023.647 2.156.953 3.39.934v3.17c-1.257.03-2.44-.208-3.553-.723-.676-.315-1.302-.706-1.876-1.171l-.01 6.681c0 3.73-2.484 6.89-6.138 7.67-3.679.785-7.38-1.184-8.642-4.69-1.29-3.574.28-7.59 3.582-9.335 1.171-.632 2.433-.94 3.785-.93v3.242c-.313-.05-.615-.04-.91.03-1.463.341-2.49 1.676-2.41 3.17.08 1.476 1.253 2.705 2.73 2.818 1.638.126 3.033-.882 3.36-2.494.055-.27.083-.55.083-.83l.01-12.741z"/>
              </svg>
            </a>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-10 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {products.map((product) => (
                <div key={product.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative w-full">
                    <img
                      src={buildCloudinaryUrl(product.imageUrl || 'https://via.placeholder.com/300x300/e2e8f0/64748b?text=Producto', { w: 60, h: 60, crop: 'fill', quality: 10, blur: 1000, dprAuto: false })}
                      srcSet={product.imageUrl ? buildCloudinarySrcSet(product.imageUrl, [400, 600, 800], { crop: 'fill' }) : ''}
                      sizes="(max-width: 768px) 50vw, 25vw"
                      alt={product.name}
                      className="w-full h-40 md:h-48 object-cover"
                      onClick={() => openDetail(product)}
                      loading="lazy"
                      decoding="async"
                      style={{ cursor: 'zoom-in' }}
                    />
                  </div>
                  <div className="p-3 md:p-6">
                    <h3 className="text-sm md:text-xl font-semibold text-gray-900 mb-1 md:mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-3 md:mb-4 text-xs md:text-sm leading-snug min-h-[38px] md:min-h-[48px] overflow-hidden">{product.desc}</p>
                    {(product.price !== undefined && product.price !== '' && !isNaN(typeof product.price === 'number' ? product.price : parseFloat(product.price))) ? (
                      <div className="flex justify-between items-center">
                        <span className="text-lg md:text-2xl font-bold" style={{ color: theme.primaryColor }}>
                          Bs. {typeof product.price === 'number' ? product.price : parseFloat(product.price)}
                        </span>
                        <button
                          onClick={() => addToCart(product)}
                          className="px-3 py-2 rounded-lg text-white text-sm md:text-base font-medium hover:scale-105 transition-transform"
                          style={{ backgroundColor: theme.primaryColor }}
                        >
                          Agregar
                        </button>
                      </div>
                    ) : (
                      (() => {
                        const waUrl = buildWhatsAppUrlForProduct(product)
                        return waUrl ? (
                          <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-center px-3 py-2 rounded-lg text-white text-sm md:text-base font-medium hover:scale-105 transition-transform"
                            style={{ backgroundColor: brandColors.whatsapp }}
                          >
                            Preguntar por este producto
                          </a>
                        ) : null
                      })()
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <ShoppingCartIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sin productos</h3>
              <p className="text-gray-600">Pronto agregaremos productos a nuestro catálogo.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">¿Tienes preguntas?</h2>
          <p className="text-lg text-gray-600 mb-12">Contáctanos directamente</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {vCard.phone && (
              <a
                href={`tel:${vCard.phone}`}
                className="p-6 border-2 border-gray-200 rounded-2xl hover:border-pink-400 transition-colors group w-full"
              >
                <PhoneIcon className="w-8 h-8 text-gray-600 group-hover:text-pink-500 mx-auto mb-4" />
                <p className="font-semibold text-gray-900">Llamar</p>
                <p className="text-sm text-gray-600">{vCard.phone}</p>
              </a>
            )}
            
            {vCard.email && (
              <a
                href={`mailto:${vCard.email}`}
                className="p-6 border-2 border-gray-200 rounded-2xl hover:border-pink-400 transition-colors group w-full"
              >
                <EnvelopeIcon className="w-8 h-8 text-gray-600 group-hover:text-pink-500 mx-auto mb-4" />
                <p className="font-semibold text-gray-900">Email</p>
                <p className="text-sm text-gray-600">{vCard.email}</p>
              </a>
            )}
            
            {socialLinks.whatsapp && (
              <a
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 border-2 border-gray-200 rounded-2xl hover:border-pink-400 transition-colors group w-full"
              >
                <svg className="w-8 h-8 text-gray-600 group-hover:text-pink-500 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.645 3.488"/>
                </svg>
                <p className="font-semibold text-gray-900">WhatsApp</p>
                <p className="text-sm text-gray-600">Chat directo</p>
              </a>
            )}

            {(vCard.location || vCard.address) && (
              <a
                href={(vCard.lat && vCard.lng) ? `https://www.google.com/maps?q=${vCard.lat},${vCard.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vCard.location || vCard.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 border-2 border-gray-200 rounded-2xl hover:border-pink-400 transition-colors group w-full"
              >
                <MapPinIcon className="w-8 h-8 text-gray-600 group-hover:text-pink-500 mx-auto mb-4" />
                <p className="font-semibold text-gray-900">Ubicación</p>
                <p className="text-sm text-gray-600 break-words whitespace-normal">{vCard.location || vCard.address}</p>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Carrito</h3>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {cartItems.length > 0 ? (
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                      <img
                        src={buildCloudinaryUrl(item.imageUrl || 'https://via.placeholder.com/60x60/e2e8f0/64748b?text=P', { w: 120, h: 120 })}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{item.name}</h4>
                        <p className="text-sm text-gray-600">Bs. {item.price} x {item.quantity}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Tu carrito está vacío</p>
                </div>
              )}
            </div>
            
            {cartItems.length > 0 && (
              <div className="border-t p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold">Total:</span>
                  <span className="text-xl font-bold" style={{ color: theme.primaryColor }}>
                    Bs. {getTotalPrice().toFixed(2)}
                  </span>
                </div>
                <button
                  className="w-full py-3 rounded-lg text-white font-medium"
                  style={{ backgroundColor: theme.primaryColor }}
                  onClick={() => {
                    const lines = cartItems.map((item) => {
                      const priceNum = typeof item.price === 'number' ? item.price : (parseFloat(item.price) || 0)
                      return `• ${item.quantity} x ${item.name} (Bs. ${priceNum.toFixed(2)})`
                    }).join('\n')
                    const msg = `Hola, quiero hacer un pedido:\n${lines}\nTotal: Bs. ${getTotalPrice().toFixed(2)}`
                    const waUrl = socialLinks.whatsapp ? `${socialLinks.whatsapp}${socialLinks.whatsapp.includes('?') ? '&' : '?'}text=${encodeURIComponent(msg)}` : ''
                    if (waUrl) {
                      window.open(waUrl, '_blank')
                    }
                  }}
                >
                  Completar Pedido por WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showProductDetail && detailProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeDetail}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div
              className="relative overflow-hidden"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onDoubleClick={handleDoubleClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ touchAction: 'none' }}
              ref={containerRef}
            >
              <img
                src={buildCloudinaryUrl(detailProduct.imageUrl || 'https://via.placeholder.com/800x600/e2e8f0/64748b?text=Producto', { w: 60, quality: 10, blur: 1000, dprAuto: false })}
                srcSet={buildCloudinarySrcSet(detailProduct.imageUrl || 'https://via.placeholder.com/800x600/e2e8f0/64748b?text=Producto', [600, 900, 1200])}
                sizes="(max-width: 768px) 100vw, 70vw"
                alt={detailProduct.name}
                className="w-full h-64 md:h-96 object-contain select-none"
                style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`, transition: dragging ? 'none' : 'transform 120ms ease-out' }}
                onLoad={handleImageLoad}
                ref={imgRef}
                draggable={false}
              />
              <div className="absolute top-3 left-3 flex gap-2">
                <button onClick={() => setZoom(z => clampZoom(z + 0.3))} className="w-10 h-10 bg-white/90 rounded-full shadow flex items-center justify-center text-gray-700 hover:bg-white" aria-label="Acercar">＋</button>
                <button onClick={() => setZoom(z => clampZoom(z - 0.3))} className="w-10 h-10 bg-white/90 rounded-full shadow flex items-center justify-center text-gray-700 hover:bg-white" aria-label="Alejar">－</button>
                <button onClick={() => { setZoom(baseScale); setOffset({ x: 0, y: 0 }) }} className="w-10 h-10 bg-white/90 rounded-full shadow flex items-center justify-center text-gray-700 hover:bg-white" aria-label="Reiniciar">⟲</button>
              </div>
              <button
                onClick={closeDetail}
                className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full shadow flex items-center justify-center text-gray-700 hover:bg-white"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 md:p-6">
              <h3 className="text-lg md:text-2xl font-semibold text-gray-900 mb-2">{detailProduct.name}</h3>
              {detailProduct.desc && (
                <p className="text-sm md:text-base text-gray-700 mb-4 leading-relaxed">{detailProduct.desc}</p>
              )}
              {(detailProduct.price !== undefined && detailProduct.price !== '' && !isNaN(typeof detailProduct.price === 'number' ? detailProduct.price : parseFloat(detailProduct.price))) ? (
                <div className="flex justify-between items-center">
                  <span className="text-xl md:text-3xl font-bold" style={{ color: theme.primaryColor }}>
                    Bs. {typeof detailProduct.price === 'number' ? detailProduct.price : parseFloat(detailProduct.price)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { addToCart(detailProduct); closeDetail() }}
                      className="px-4 py-2 rounded-lg text-white font-medium hover:scale-105 transition-transform"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      Agregar al carrito
                    </button>
                    <button
                      onClick={closeDetail}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end">
                  <button
                    onClick={closeDetail}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <PWAInstallButton vCard={vCard} />
    </div>
  )
}

CatalogTemplate.propTypes = {
  vCard: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
}

export default CatalogTemplate
